/**
 * The customer app's top bar.
 *
 * One bar on every screen, so the language and appearance control sits in the
 * same place whether the customer scanned a QR and landed straight on a menu or
 * opened the app and started at the landing page. Before this, the two settings
 * were two pills on one screen and a panel under the list on another. Checkout
 * is the one exception: it is a dialog over the menu, so it carries no control
 * of its own.
 *
 * The bar is passed to `Screen`'s `header` slot, which pins it above the scroll
 * view — so it stays put while a long menu or a long order list moves under it.
 */

import { useState, type ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { ChevronLeft, X } from 'lucide-react-native';
import {
  Icon,
  PreferencesButton,
  PreferencesSheet,
  Text,
  cn,
  useT,
} from '@yummy/ui-rnr';

/**
 * Renders the title slot, the leading control, and the preferences control.
 *
 * The leading control is Back on a screen and Close on a dialog. The title is
 * centred on the bar and rides over its full width, with an optional quieter
 * line beneath it.
 *
 * @param props The title and subtitle, an optional back or close handler, extra
 *   classes, and whether the preferences control is carried.
 * @returns The pinned top bar.
 */
export function AppBar({
  title,
  subtitle,
  onBack,
  onClose,
  className,
  preferences = true,
}: {
  /**
   * A string is a screen name and takes the bar's own type. A node is rendered
   * as given, for a screen whose title is not plain text — the landing page
   * passes its wordmark, which is set in the brand colour and a larger size.
   */
  title?: ReactNode;
  /**
   * A second, quieter line under the title — the restaurant an order is from.
   *
   * Passing one is what makes the bar two lines tall, and the line is held open
   * from the first paint so a name arriving with the data does not move the
   * title under it. Screens that pass nothing keep the single-line bar.
   */
  subtitle?: ReactNode;
  /** When given, a back control is shown on the left. */
  onBack?: () => void;
  /**
   * When given, a close control is shown instead of the back one.
   *
   * For a dialog: it dismisses rather than navigates, which is what the X says
   * and a chevron does not.
   */
  onClose?: () => void;
  className?: string;
  /**
   * Whether the bar carries the language and appearance control.
   *
   * On by default, because on a screen it is how the customer reaches a setting
   * that is otherwise nowhere. Checkout turns it off: it is a dialog over the
   * menu now, so it is a step in a task rather than a place, and the control is
   * waiting on the screen it was opened from.
   */
  preferences?: boolean;
}) {
  const t = useT();
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const leading = onClose ? (
    <Pressable
      onPress={onClose}
      accessibilityRole="button"
      accessibilityLabel={t('common.close')}
      hitSlop={8}
      // `shadow-sm shadow-black/5` is what the `outline` Button the preferences
      // control uses carries; the two ends of the bar are built differently, so
      // the shadow has to be asked for here rather than inherited.
      className="border-border bg-background shadow-sm shadow-black/5 h-[45px] w-[45px] flex-none items-center justify-center rounded-full border active:opacity-70">
      <Icon as={X} size={22} className="text-muted-foreground" />
    </Pressable>
  ) : onBack ? (
    <Pressable
      onPress={onBack}
      accessibilityRole="button"
      accessibilityLabel={t('common.back')}
      hitSlop={8}
      // `shadow-sm shadow-black/5` is what the `outline` Button the preferences
      // control uses carries; the two ends of the bar are built differently, so
      // the shadow has to be asked for here rather than inherited.
      className="border-border bg-background shadow-sm shadow-black/5 h-[45px] w-[45px] flex-none items-center justify-center rounded-full border active:opacity-70">
      <Icon as={ChevronLeft} size={22} className="text-muted-foreground" />
    </Pressable>
  ) : null;

  // `h-[45px] w-[45px]` matches the back control rather than the kit's
  // `size="icon"` (40 on a phone, 36 from the tablet breakpoint), so the two ends
  // of the bar are the same disc rather than 5px apart at one width. It also
  // clears the 44px touch-target minimum, which 36 did not. The `sm:` pair is
  // needed as well, or the variant's `sm:h-9 sm:w-9` takes the box back at the
  // tablet breakpoint.
  const trailing = preferences ? (
    <PreferencesButton
      onPress={() => setPreferencesOpen(true)}
      className="h-[45px] w-[45px] sm:h-[45px] sm:w-[45px]"
    />
  ) : null;

  return (
    <>
      {/*
        A full-width bar with a fixed gutter, which is how the cart bar at the
        other end of the screen already behaves. Chasing the scroll content's
        column instead is not worth it: the column moves with the viewport, is
        capped at a width the bar would have to mirror, and shifts by half a
        scrollbar on any browser whose scrollbars take up layout — so the bar
        would spend its time almost, but not quite, lining up. Two bars with the
        same gutter frame the screen, and both stay put.
      */}
      <View
        className={cn(
          // A fixed 64, whatever the bar holds: a 45px control leaves 9.5px above
          // and below it, and the two-line title's 48px stack leaves 8 — both
          // comfortable, and the bar no longer changes height for a subtitle.
          'bg-background relative h-16 w-full flex-row items-center justify-between gap-3 px-4',
          className,
        )}>
        {/* The title rides over the whole bar rather than in the space between the
            controls, so a long restaurant name has the full width to run in and
            still centres on the bar. It paints under the controls — which are
            opaque and sit above it — and takes no pointer events of its own. */}
        <View
          pointerEvents="none"
          className="absolute inset-x-0 top-0 bottom-0 items-center justify-center px-4">
          <View className="w-full items-center">
            {typeof title === 'string' ? (
              <Text numberOfLines={1} className="w-full text-center text-xl font-semibold">
                {title}
              </Text>
            ) : (
              // A node title — the landing page's wordmark — centred as it stands.
              title
            )}

            {subtitle !== undefined ? (
              // `h-5` keeps the line whether or not the name has arrived, so a
              // checkout does not change height when its data lands.
              <Text
                numberOfLines={1}
                // `text-sm`, the scale's own step: this was a one-off 13.
                className="text-muted-foreground h-5 w-full text-center text-sm">
                {subtitle}
              </Text>
            ) : null}
          </View>
        </View>

        <View className="relative z-10 h-[45px] w-[45px] flex-none items-center justify-center">
          {leading}
        </View>
        <View className="relative z-10 h-[45px] w-[45px] flex-none items-center justify-center">
          {trailing}
        </View>
      </View>

      {/* Deliberately outside the row above. The sheet renders an element where
          it is declared; as a child of that `flex-row` it would count as an item
          and the row's `gap-3` would push the button 12px in from the edge. Out
          here it is a zero-height sibling of the bar, which changes nothing. */}
      {preferences ? (
        <PreferencesSheet visible={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
      ) : null}
    </>
  );
}
